import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, FileText, Search, User, Wrench } from "lucide-react";
import { api } from "@/lib/api";

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      return;
    }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    setQ("");
    nav(to);
  };

  const groups = res
    ? [
        { key: "vehicles", icon: Car, label: "Véhicules", items: res.vehicles, title: (v: any) => v.plate, sub: (v: any) => `${v.brand} ${v.model}`, to: (v: any) => `/vehicules/${v.id}` },
        { key: "clients", icon: User, label: "Clients", items: res.clients, title: (c: any) => c.name, sub: (c: any) => c.phone, to: (c: any) => `/clients/${c.id}` },
        { key: "orders", icon: Wrench, label: "Ordres de réparation", items: res.orders, title: (o: any) => o.number, sub: (o: any) => o.mechanic_name || "", to: (o: any) => `/or/${o.id}` },
        { key: "invoices", icon: FileText, label: "Factures", items: res.invoices, title: (i: any) => i.number || "Brouillon", sub: (i: any) => i.kind, to: (i: any) => `/factures/${i.id}` },
      ].filter((g) => g.items?.length)
    : [];

  return (
    <div ref={box} className="relative w-full max-w-xl">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Immatriculation, client, n° d'OR, facture…"
        className="h-11 w-full rounded-sm border border-border bg-card pl-10 pr-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
        data-testid="global-search-input"
      />
      {open && res && (
        <div className="absolute left-0 right-0 top-12 z-50 max-h-[70vh] overflow-y-auto rounded-sm border border-border bg-popover shadow-2xl" data-testid="global-search-results">
          {groups.length === 0 && <div className="p-4 text-sm text-muted-foreground">Aucun résultat</div>}
          {groups.map((g) => (
            <div key={g.key} className="border-b border-border last:border-0">
              <div className="eyebrow px-4 pt-3 pb-1">{g.label}</div>
              {g.items.map((it: any) => (
                <button key={it.id} onClick={() => go(g.to(it))} className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-accent" data-testid={`search-result-${g.key}-${it.id}`}>
                  <g.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono-data text-sm font-semibold">{g.title(it)}</span>
                  <span className="truncate text-xs text-muted-foreground">{g.sub(it)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
